import type { FC } from "react";
import { useState, useEffect } from "react";
import { getSetting, setSetting, refreshSettings } from "../taskpane";
import { useAppContext } from "../contexts/AppContext";
import { useTheme, type ThemeMode, useSizing, type Sizing } from "../contexts/ThemeContext";
import "../styles/SettingsView.css";

export type FolderSortMode = "alphabetical" | "recent";
export type FolderSortOrder = "asc" | "desc";

export interface SettingsPreferences {
    smartSuggestionsEnabled: boolean;
    skipArchiveConfirmation: boolean;
    folderSortMode: FolderSortMode;
    folderSortOrder: FolderSortOrder;
    showArchiveNotifications: boolean;
    deleteArchivedEmails: boolean;
}

// Default settings
const DEFAULT_SETTINGS: SettingsPreferences = {
    smartSuggestionsEnabled: true,
    skipArchiveConfirmation: false,
    folderSortMode: "alphabetical",
    folderSortOrder: "asc",
    showArchiveNotifications: true,
    deleteArchivedEmails: false,
};

const SettingsView: FC = () => {
    const { navigateToFolders } = useAppContext();
    const { themeMode, setThemeMode } = useTheme();
    const { sizing, setSizing } = useSizing();
    // Initialize settings from persistent storage
    const [settings, setSettings] = useState<SettingsPreferences>(() => ({
        smartSuggestionsEnabled: getSetting('smartSuggestionsEnabled', DEFAULT_SETTINGS.smartSuggestionsEnabled),
        skipArchiveConfirmation: getSetting('skipArchiveConfirmation', DEFAULT_SETTINGS.skipArchiveConfirmation),
        folderSortMode: getSetting('folderSortMode', DEFAULT_SETTINGS.folderSortMode),
        folderSortOrder: getSetting('folderSortOrder', DEFAULT_SETTINGS.folderSortOrder),
        showArchiveNotifications: getSetting('showArchiveNotifications', DEFAULT_SETTINGS.showArchiveNotifications),
        deleteArchivedEmails: getSetting('deleteArchivedEmails', DEFAULT_SETTINGS.deleteArchivedEmails),
    }));

    // Function to reload settings from persistent storage
    const reloadSettings = () => {
        setSettings({
            smartSuggestionsEnabled: getSetting('smartSuggestionsEnabled', DEFAULT_SETTINGS.smartSuggestionsEnabled),
            skipArchiveConfirmation: getSetting('skipArchiveConfirmation', DEFAULT_SETTINGS.skipArchiveConfirmation),
            folderSortMode: getSetting('folderSortMode', DEFAULT_SETTINGS.folderSortMode),
            folderSortOrder: getSetting('folderSortOrder', DEFAULT_SETTINGS.folderSortOrder),
            showArchiveNotifications: getSetting('showArchiveNotifications', DEFAULT_SETTINGS.showArchiveNotifications),
            deleteArchivedEmails: getSetting('deleteArchivedEmails', DEFAULT_SETTINGS.deleteArchivedEmails),
        });
    };

    // Refresh settings from Office when component mounts
    useEffect(() => {
        refreshSettings();
        reloadSettings();
    }, []);

    const handleCheckbox = (key: keyof SettingsPreferences) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;
        setSettings(prev => ({ ...prev, [key]: newValue }));
        setSetting(key, newValue);
    };

    const handleSelect = <K extends "folderSortMode" | "folderSortOrder">(key: K) => (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newValue = e.target.value as SettingsPreferences[K];
        setSettings(prev => ({ ...prev, [key]: newValue }));
        setSetting(key, newValue);
    };

    return (
        <div className="settings-container">
            <div className="settings-header">
                <button className="settings-backButton" onClick={navigateToFolders}>
                    ← Back
                </button>
                <h2>Settings</h2>
            </div>

            <div className="settings-content">
                <div className="settings-section">
                    <h3>Appearance</h3>
                    <p>Choose how E-File looks. The "Default" theme follows your operating system's theme setting.</p>
                    <div className="settings-selectControl">
                        <label htmlFor="theme-mode">Theme</label>
                        <select id="theme-mode" value={themeMode} onChange={(e) => setThemeMode(e.target.value as ThemeMode)}>
                            <option value="system">Default (System)</option>
                            <option value="light">Light</option>
                            <option value="dark">Dark</option>
                        </select>
                    </div>
                    <div className="settings-selectControl">
                        <label htmlFor="sizing">Sizing</label>
                        <select id="sizing" value={sizing} onChange={(e) => setSizing(e.target.value as Sizing)}>
                            <option value="small">Small</option>
                            <option value="default">Default</option>
                            <option value="large">Large</option>
                        </select>
                    </div>
                </div>

                <div className="settings-section">
                    <h3>Smart Suggestions</h3>
                    <p>Let E-File highlight emails that likely belong to an existing project by matching the project number or keyword in the subject or body.</p>
                    <label className="settings-toggleLabel">
                        <input type="checkbox" checked={settings.smartSuggestionsEnabled} onChange={handleCheckbox("smartSuggestionsEnabled")} />
                        Suggest emails to archive when a project number or keyword is detected
                    </label>
                </div>

                <div className="settings-section">
                    <h3>Archive Confirmations</h3>
                    <p>When archiving emails with a folder selected, or dropping emails onto a favorite, you can skip the confirmation screen</p>
                    <label className="settings-toggleLabel">
                        <input type="checkbox" checked={settings.skipArchiveConfirmation} onChange={handleCheckbox("skipArchiveConfirmation")} />
                        Skip confirmations when archiving
                    </label>
                </div>

                <div className="settings-section">
                    <h3>Folder Sorting</h3>
                    <p>Choose how folders appear in the navigator. Sorting happens instantly in the main view.</p>
                    <div className="settings-selectGroup">
                        <div className="settings-selectControl">
                            <label htmlFor="sort-mode">Sort By</label>
                            <select id="sort-mode" value={settings.folderSortMode} onChange={handleSelect("folderSortMode")}>
                                <option value="alphabetical">Alphabetical</option>
                                <option value="recent">Most Recent Activity</option>
                            </select>
                        </div>
                        <div className="settings-selectControl">
                            <label htmlFor="sort-order">Order</label>
                            <select id="sort-order" value={settings.folderSortOrder} onChange={handleSelect("folderSortOrder")}>
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="settings-section">
                    <h3>Post-Archive Cleanup</h3>
                    <p className="settings-delete-disclaimer">Note: Deleted messages will be in your 'Deleted Items' folder.</p>
                    <p>Control whether emails are removed from the workspace once the archive request is successful.</p>
                    <label className="settings-toggleLabel">
                        <input type="checkbox" checked={settings.deleteArchivedEmails} onChange={handleCheckbox("deleteArchivedEmails")} />
                        Delete successfully archived emails from the inbox
                    </label>
                </div>
            </div>
        </div>
    );
};

export default SettingsView;
